import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  CircularProgress,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useState, useEffect, useRef } from 'react';

interface InformationDialogProps {
  data?: any;
  onClose: (_value: boolean) => void;
  open: boolean;
}

const InformationDialog = (props: InformationDialogProps) => {
  const { data, onClose, open } = props;

  const [loading, setLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setLoading(false);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.value = '';
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  const handleClose = () => {
    if (loading) return;
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onClose(false);
  };

  const handleConfirm = async () => {
    const inputValue = inputRef.current?.value || '';
    const trimmedInput = inputValue.trim();
    
    if (!trimmedInput) {
      return;
    }
    
    if (data?.onConfirm) {
      setLoading(true);
      try {
        const success = await data.onConfirm(trimmedInput);
        setLoading(false);
        
        if (success !== false) {
          if (inputRef.current) {
            inputRef.current.value = '';
          }
          onClose(false);
        }
      } catch (error) {
        console.error('Error in dialog confirm:', error);
        setLoading(false);
      }
    } else {
      onClose(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 0,
          minWidth: { xs: '90%', sm: 400 },
          maxWidth: 440,
          bgcolor: 'white',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2.5,
          pb: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {data?.icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: '#fdf2f8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#E91E8C',
              }}
            >
              {data.icon}
            </Box>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#1e293b',
              fontSize: '1.1rem',
            }}
          >
            {data?.title}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          size="small"
          sx={{
            color: '#94a3b8',
            '&:hover': { bgcolor: '#f1f5f9' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        {data?.description && (
          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              mb: 2.5,
              lineHeight: 1.6,
            }}
          >
            {data.description}
          </Typography>
        )}

        {data?.setInput && (
          <TextField
            label={data.inputPlaceholder}
            variant="outlined"
            fullWidth
            inputRef={inputRef}
            disabled={loading}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !loading) {
                handleConfirm();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': { borderColor: '#e2e8f0' },
                '&:hover fieldset': { borderColor: '#cbd5e1' },
                '&.Mui-focused fieldset': { borderColor: '#E91E8C' },
              },
              '& .MuiInputLabel-root': {
                color: '#64748b',
                '&.Mui-focused': { color: '#E91E8C' },
              },
            }}
          />
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{
              py: 1.25,
              borderRadius: 2,
              borderColor: '#e2e8f0',
              color: '#64748b',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f8fafc',
              },
              '&:disabled': {
                borderColor: '#e2e8f0',
                color: '#cbd5e1',
              },
            }}
          >
            {data?.cancelText || 'Cancel'}
          </Button>
          
          {data?.onConfirm && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleConfirm}
              disabled={loading}
              sx={{
                py: 1.25,
                borderRadius: 2,
                bgcolor: '#E91E8C',
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 2px 8px rgba(233, 30, 140, 0.25)',
                '&:hover': {
                  bgcolor: '#c41874',
                  boxShadow: '0 4px 12px rgba(233, 30, 140, 0.35)',
                },
                '&:disabled': {
                  bgcolor: '#e2e8f0',
                  color: '#94a3b8',
                  boxShadow: 'none',
                },
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Confirm'
              )}
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default InformationDialog;
